import React from 'react';

const PaymentFailureModal = ({ show, onClose, errorType, errorMessage }) => {
  if (!show) return null;

  const getErrorContent = () => {
    switch (errorType) {
      case 'cancelled':
        return {
          title: '❌ Paiement Annulé',
          message: 'Le paiement a été annulé.',
          description: 'Vous avez annulé le processus de paiement. Aucune transaction n\'a été effectuée.',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          titleColor: 'text-yellow-800',
          messageColor: 'text-yellow-700',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
          icon: '⚠️'
        };
      case 'failed':
        return {
          title: '🚫 Échec du Paiement',
          message: 'Le paiement n\'a pas pu être traité.',
          description: errorMessage || 'Une erreur est survenue lors du traitement de votre paiement. Cela peut être dû à un problème avec votre carte bancaire ou une erreur technique.',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          titleColor: 'text-red-800',
          messageColor: 'text-red-700',
          buttonColor: 'bg-red-600 hover:bg-red-700',
          icon: '❌'
        };
      case 'session_error':
        return {
          title: '⚠️ Erreur de Session',
          message: 'Impossible de créer la session de paiement.',
          description: errorMessage || 'Une erreur technique est survenue lors de la création de votre session de paiement. Veuillez réessayer dans quelques instants.',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          titleColor: 'text-orange-800',
          messageColor: 'text-orange-700',
          buttonColor: 'bg-orange-600 hover:bg-orange-700',
          icon: '⚙️'
        };
      case 'network_error':
        return {
          title: '🌐 Erreur de Connexion',
          message: 'Problème de connexion réseau.',
          description: 'Impossible de communiquer avec le serveur de paiement. Vérifiez votre connexion internet et réessayez.',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          titleColor: 'text-blue-800',
          messageColor: 'text-blue-700',
          buttonColor: 'bg-blue-600 hover:bg-blue-700',
          icon: '🌐'
        };
      default:
        return {
          title: '⚠️ Erreur de Paiement',
          message: 'Une erreur inattendue est survenue.',
          description: errorMessage || 'Nous n\'avons pas pu traiter votre demande de paiement. Veuillez réessayer ou contacter le support.',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          titleColor: 'text-red-800',
          messageColor: 'text-red-700',
          buttonColor: 'bg-red-600 hover:bg-red-700',
          icon: '❗'
        };
    }
  };

  const content = getErrorContent();

  const getSuggestions = () => {
    switch (errorType) {
      case 'cancelled':
        return [
          '• Cliquez sur "Payer en ligne" pour reprendre le paiement',
          '• Ou choisissez "Paiement manuel" si vous préférez'
        ];
      case 'failed':
        return [
          '• Vérifiez les informations de votre carte bancaire',
          '• Essayez avec une autre carte si possible',
          '• Contactez votre banque si le problème persiste',
          '• Vous pouvez aussi choisir "Paiement manuel"'
        ];
      case 'session_error':
        return [
          '• Actualisez la page et réessayez',
          '• Vérifiez votre connexion internet',
          '• Contactez le support si le problème persiste'
        ];
      case 'network_error':
        return [
          '• Vérifiez votre connexion internet',
          '• Réessayez dans quelques instants',
          '• Utilisez un autre réseau si possible'
        ];
      default:
        return [
          '• Actualisez la page et réessayez',
          '• Contactez le support si le problème persiste'
        ];
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className={`${content.bgColor} ${content.borderColor} border rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all duration-300 ease-out scale-100`}>
        {/* Header avec icône */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-3">{content.icon}</div>
          <h2 className={`text-xl font-bold ${content.titleColor} mb-2`}>
            {content.title}
          </h2>
          <p className={`text-base ${content.messageColor} font-medium`}>
            {content.message}
          </p>
        </div>

        {/* Description */}
        <div className="mb-6">
          <p className={`${content.messageColor} text-center leading-relaxed text-sm`}>
            {content.description}
          </p>
        </div>

        {/* Suggestions d'actions */}
        <div className={`bg-white bg-opacity-60 rounded-lg p-4 mb-6 ${content.borderColor} border`}>
          <h4 className={`font-semibold ${content.titleColor} mb-3 text-sm`}>
            💡 Que faire maintenant ?
          </h4>
          <ul className={`${content.messageColor} space-y-1 text-xs`}>
            {getSuggestions().map((suggestion, index) => (
              <li key={index}>{suggestion}</li>
            ))}
          </ul>
        </div>

        {/* Boutons d'action */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onClose}
            className={`w-full ${content.buttonColor} text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50`}
          >
            Fermer
          </button>

          {errorType === 'cancelled' && (
            <button
              onClick={() => {
                onClose();
                // L'action de reprendre le paiement peut être ajoutée ici
                window.location.reload(); // Recharge la page pour reprendre
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Reprendre le paiement
            </button>
          )}
        </div>

        {/* Contact support */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Besoin d'aide ? Contactez-nous à{' '}
            <a href="mailto:info@boaz-study.com" className="text-blue-600 hover:underline">
              info@boaz-study.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailureModal;